import { BedType } from '@data'
import { useBed } from '@hook'
import { yupResolver } from '@hookform/resolvers/yup'
import { useAppSelector } from '@store/hooks'
import { palette } from '@themes'
import { BedResMock } from '@type/bed.type'
import { Button, CheckBox, FormButton, Input, Modal, SelectInputField, TextAreaField } from '@ui'
import { getError, showToast } from '@util'
import { Box, DropButton, Form, Text } from 'grommet'
import { Checkmark, FormDown } from 'grommet-icons'
import { useMemo, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { bedSchema, bedStatus } from './AddBed'

type Props = {
  onClose: () => void
  defaultData?: BedResMock
  defaultIncluded?: any
}

export const EditBed = ({ onClose, defaultData }: Props) => {
  const methods = useForm({
    defaultValues: {
      bedType: defaultData?.attributes.bed_type || '',
      description: defaultData?.attributes.description || '',
      status: defaultData?.meta.status || '',
      telemetry: defaultData?.attributes.telemetry || false
      // otherText: defaultData?.attributes.bed_type === BedType.Other && defaultData?.attributes.other_text || ''
    },
    resolver: yupResolver(bedSchema)
  })
  const bedType = useAppSelector(state => state.config.bed_types)
  const [otherText, setOtherText] = useState<BedType>()
  const [showDrop, setShowDrop] = useState(false)
  const { updateBed } = useBed({})

  const bedTypes = useMemo(() => {
    return [...bedType, 'Other']
  }, [bedType])

  const {
    control,
    watch,
    setValue,
    handleSubmit,
    formState: { isSubmitting, errors }
  } = methods

  const bed = watch('bedType')

  const onSubmit = async (formData: any) => {
    const payload = {
      data: {
        attributes: {
          bed_type: formData.bedType,
          description: formData.description,
          status: formData.status.toLowerCase(),
          telemetry: formData.telemetry
        },
        relationships: {
          unit: {
            data: {
              id: formData.unit,
              type: 'units'
            }
          }
        },
        type: 'beds'
      }
    }

    try {
      await updateBed({ data: payload, id: defaultData?.id as string })
      onClose()
      showToast('Bed updated successfully')
    } catch (error) {
      getError(error)
    }
  }

  return (
    <FormProvider {...methods}>
      <Modal onClose={onClose} title="Edit Bed">
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Box overflow="auto" pad={{ horizontal: '32px', vertical: '16px' }} width={'530px'}>
            <Box className="mx-auto w-full max-w-[399px]" flex={false} gap={'20px'}>
              <Box>
                <Text margin={{ bottom: '2px' }} style={{ fontSize: '14px', fontWeight: 500 }}>
                  Bed
                </Text>
                <DropButton
                  dropContent={
                    <Box flex={false}>
                      {bedTypes?.map(item => (
                        <Box
                          key={item}
                          onClick={() => {
                            setValue('bedType', item as NonNullable<BedType | undefined>)
                            if (item !== 'Other') {
                              setShowDrop(false)
                            }
                          }}
                          style={{
                            background: bed === item ? '#eeeeee' : 'none',
                            cursor: 'pointer',
                            fontSize: '16px',
                            padding: '10px 12px'
                          }}
                        >
                          {item}
                        </Box>
                      ))}
                      {bed === 'Other' && (
                        <Box gap={'12px'} margin={{ vertical: '16px' }} pad={{ horizontal: '14px' }}>
                          <Input onChange={e => setOtherText(e.target?.value as BedType)} value={otherText} />
                          {errors.otherText?.message && (
                            <Box pad={{ left: 'small' }}>
                              <Text color="status-error" size="small">
                                {errors.otherText?.message}
                              </Text>
                            </Box>
                          )}
                          <Box direction="row" justify="start">
                            <Button
                              icon={<Checkmark size="small" />}
                              label="Save"
                              onClick={() => {
                                setValue('bedType', otherText as NonNullable<BedType | undefined>)
                                setShowDrop(false)
                              }}
                              type="button"
                            />
                          </Box>
                        </Box>
                      )}
                    </Box>
                  }
                  dropProps={{
                    align: { top: 'bottom' },
                    height: '400px',
                    margin: { left: '0px' },
                    style: { maxWidth: '400px' }
                  }}
                  onClose={() => setShowDrop(false)}
                  onOpen={() => setShowDrop(true)}
                  open={showDrop}
                  style={{
                    border: `1px solid rgba(0, 0, 0, 0.33)`,
                    borderRadius: '100px',
                    margin: '0',
                    padding: '10px 12px',
                    width: '100%'
                  }}
                >
                  <Box align="center" direction="row" flex justify="between" width={'100%'}>
                    <Box>
                      {bed && bed !== 'Other' ? (
                        <Text size="small" style={{ textTransform: 'capitalize' }} weight={500}>
                          {bed}
                        </Text>
                      ) : (
                        <Text color={'#aaaaaa'} size="small" weight={600}>
                          Select a bed type
                        </Text>
                      )}
                    </Box>
                    <FormDown color={palette.brand} />
                  </Box>
                </DropButton>
              </Box>
              <Box>
                <SelectInputField
                  dropProps={{
                    align: { left: 'left', right: 'right', top: 'bottom' }
                  }}
                  label="Status"
                  labelKey={'name'}
                  name="status"
                  options={bedStatus}
                  placeholder="Select status"
                  valueKey={{ key: 'value', reduce: true }}
                />
              </Box>
              <Box>
                <TextAreaField label="Description (optional)" name="description" />
              </Box>
              <Controller
                control={control}
                name="telemetry"
                render={({ field: { onChange, value } }) => (
                  <Box direction="row" gap={'4px'}>
                    <CheckBox defaultCheck={value} handleChange={onChange} />
                    <Text size="small">Telemetry</Text>
                  </Box>
                )}
              />
            </Box>
          </Box>
          <FormButton isLoading={isSubmitting} onClose={onClose} submit="Update" />
        </Form>
      </Modal>
    </FormProvider>
  )
}
